/**
 * Hoists `lang` off a <span> wrapping an entire rich-text value, so the
 * attribute lands on the element a screen reader announces.
 *
 * Walks the span tags rather than just matching first/last: for two sibling
 * spans a naive match spans both, which emits unbalanced HTML and applies the
 * first language to everything. Anything but a single wrapper is left alone —
 * inner spans keep their own lang and still work.
 */
export const extractLangFromSpan = (html: string): { lang: string | null; content: string } => {
    const noLang = { lang: null, content: html };
    const trimmed = html.trim();
    const open = trimmed.match(/^<span lang="([^"]+)"\s*>/);

    if (!open || !trimmed.endsWith('</span>')) {
        return noLang;
    }

    const tags = /<span\b[^>]*>|<\/span>/gi;
    let depth = 0;
    let tag: RegExpExecArray | null;

    while ((tag = tags.exec(trimmed)) !== null) {
        depth += tag[0].startsWith('</') ? -1 : 1;

        // Balanced before the end of the string: siblings, not a single wrapper.
        if (depth === 0 && tag.index + tag[0].length < trimmed.length) {
            return noLang;
        }
    }

    if (depth !== 0) {
        return noLang;
    }

    const content = trimmed.slice(open[0].length, -'</span>'.length);

    return content ? { lang: open[1], content } : noLang;
};
